import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Eye, EyeOff, ArrowRight } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import BrandLogo from '../components/branding/BrandLogo';
import TrustStrip from '../components/branding/TrustStrip';
import VisualIntelligenceHero from '../components/branding/VisualIntelligenceHero';
import { BRAND } from '../assets/branding';

const SHOW_DEMO = process.env.REACT_APP_SHOW_DEMO_CREDENTIALS === 'true';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const routeForRole = (role) =>
    role === 'SuperAdmin' ? '/superadmin/dashboard'
      : role === 'CompanyAdmin' ? '/admin/dashboard'
      : '/app/dashboard';

  useEffect(() => {
    if (user) navigate(user.firstLoginRequired ? '/first-login-reset' : routeForRole(user.role));
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate(result.firstLoginRequired ? '/first-login-reset' : routeForRole(result.user.role));
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left brand-experience panel (desktop only) */}
      <div className="hidden lg:block">
        <VisualIntelligenceHero />
      </div>

      {/* Right authentication panel */}
      <div className="relative flex flex-col min-h-screen">
        <div className="absolute top-4 right-4 z-10 flex items-center gap-1">
          <ThemeToggle />
          <LanguageSwitcher authed={false} />
        </div>

        <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 sm:px-10">
          <div className="w-full max-w-md space-y-8">
            {/* Mobile-first top branding (hidden on desktop where the left panel carries it) */}
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <BrandLogo variant="full" imgClassName="h-16 sm:h-20 w-auto" data-testid="login-logo" />
              <p className="text-sm text-muted-foreground mt-3 lg:hidden">{BRAND.tagline}</p>
            </div>

            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
                {t('auth.welcome')}
              </h1>
              <p className="text-base text-muted-foreground mt-2">{t('auth.signInSubtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form" noValidate>
              {error && (
                <div
                  className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
                  role="alert"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="login-email" className="text-sm font-semibold text-foreground mb-2 block">
                  {t('auth.emailLabel')}
                </label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 w-full px-4 bg-background border border-border rounded-md text-base outline-none transition-all focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  placeholder={t('auth.emailPlaceholder')}
                  autoComplete="email"
                  required
                  aria-invalid={!!error}
                  data-testid="email-input"
                />
              </div>

              <div>
                <label htmlFor="login-password" className="text-sm font-semibold text-foreground mb-2 block">
                  {t('auth.passwordLabel')}
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 w-full px-4 pr-12 bg-background border border-border rounded-md text-base outline-none transition-all focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    aria-invalid={!!error}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    aria-pressed={showPassword}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <a href="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                    {t('auth.forgotPassword')}
                  </a>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="h-12 w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md shadow-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('auth.signingIn')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('auth.signIn')}</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {SHOW_DEMO && (
              <div className="pt-4 border-t border-border" data-testid="demo-credentials">
                <p className="text-xs text-muted-foreground text-center mb-3">{t('auth.demoCredentials')}</p>
                <div className="space-y-2 text-xs">
                  {[
                    ['SuperAdmin', 'superadmin@reasonvisionx.com / Welcome@123'],
                    ['CompanyAdmin', 'admin@democonstruction.com / Welcome@123'],
                    ['User', 'user@democonstruction.com / Welcome@123'],
                  ].map(([role, creds]) => (
                    <div key={role} className="bg-muted p-3 rounded-md">
                      <p className="font-semibold text-foreground">{role}</p>
                      <p className="text-muted-foreground">{creds}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trust strip */}
        <div className="px-6 pb-6">
          <TrustStrip />
          <p className="mt-4 text-center text-xs text-muted-foreground" data-testid="legal-links">
            <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="privacy-policy-link">Privacy Policy</a>
            <span className="mx-2">·</span>
            <a href="/data-deletion.html" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="data-deletion-link">Data Deletion</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
